'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Plus, Trash2, ImagePlus, AudioLines, X, ChevronDown, ChevronUp } from 'lucide-react';
import { createPanel, updatePanel, deletePanel, uploadPanelImage } from '@/lib/actions';

interface Panel {
  id: number;
  scene_id: number;
  order_index: number;
  title?: string | null;
  content?: string | null;
  image_path?: string | null;
  audio_path?: string | null;
}

interface VoiceLine {
  id: number;
  character_id: number;
  character_name: string;
  audio_path: string;
  label: string | null;
  created_at: string;
}

interface Props {
  panels: Panel[];
  sceneId: string;
  arcId: string;
  onTextChange: (text: string) => void;
}

function VoicePicker({
  onPick, onClose,
}: {
  onPick: (line: VoiceLine) => void;
  onClose: () => void;
}) {
  const [lines, setLines] = useState<VoiceLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [openChar, setOpenChar] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/voice/lines')
      .then(r => r.json())
      .then(data => { setLines(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Group by character
  const byChar: Record<string, VoiceLine[]> = {};
  for (const l of lines) {
    if (!byChar[l.character_name]) byChar[l.character_name] = [];
    byChar[l.character_name].push(l);
  }
  const charNames = Object.keys(byChar).sort();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <AudioLines size={16} className="text-purple-400" />
            <span className="font-bold text-sm">Pick a Voice Line</span>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {loading && <p className="text-xs text-slate-500 text-center py-8">Loading…</p>}
          {!loading && charNames.length === 0 && (
            <p className="text-xs text-slate-500 text-center py-8 italic">
              No saved voice lines yet. Record a performance in Voice Studio and save it to the library first!
            </p>
          )}
          {charNames.map(name => (
            <div key={name} className="bg-slate-800/50 rounded-xl overflow-hidden border border-slate-800">
              <button
                onClick={() => setOpenChar(openChar === name ? null : name)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-800 transition-colors"
              >
                <span className="text-sm font-bold text-slate-200">{name}</span>
                <span className="flex items-center gap-1.5 text-xs text-slate-500">
                  {byChar[name].length} line{byChar[name].length !== 1 ? 's' : ''}
                  {openChar === name ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </span>
              </button>
              {openChar === name && (
                <div className="border-t border-slate-800 divide-y divide-slate-800">
                  {byChar[name].map(line => (
                    <div key={line.id} className="px-4 py-2.5 space-y-1.5">
                      {line.label && <p className="text-xs font-bold text-slate-300">{line.label}</p>}
                      <audio controls src={`/${line.audio_path}`} className="w-full h-7" />
                      <button
                        onClick={() => onPick(line)}
                        className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 font-bold transition-colors"
                      >
                        <AudioLines size={11} /> Attach to panel
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PanelEditor({ panels: initialPanels, sceneId, arcId, onTextChange }: Props) {
  const router = useRouter();

  const [localValues, setLocalValues] = useState<Record<number, { title: string; content: string }>>(
    () => Object.fromEntries(initialPanels.map(p => [p.id, { title: p.title ?? '', content: p.content ?? '' }]))
  );
  const [panelAudio, setPanelAudio] = useState<Record<number, string | null>>(
    () => Object.fromEntries(initialPanels.map(p => [p.id, p.audio_path ?? null]))
  );
  const [pickerForPanel, setPickerForPanel] = useState<number | null>(null);

  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const handleFieldChange = (panelId: number, field: 'title' | 'content', value: string) => {
    const updated = { ...localValues[panelId], [field]: value };
    setLocalValues(prev => ({ ...prev, [panelId]: updated }));
    if (field === 'content') {
      const allText = Object.values({ ...localValues, [panelId]: updated }).map(v => v.content).join(' ');
      onTextChange(allText);
    }
    clearTimeout(debounceTimers.current[panelId]);
    debounceTimers.current[panelId] = setTimeout(async () => {
      const fd = new FormData();
      fd.set('title', updated.title);
      fd.set('content', updated.content);
      await updatePanel(String(panelId), fd);
    }, 800);
  };

  const handleAdd = async () => {
    const fd = new FormData();
    fd.set('title', ''); fd.set('content', '');
    await createPanel(sceneId, fd);
    router.refresh();
  };

  const handleDelete = async (panelId: number) => {
    if (!confirm('Delete this panel?')) return;
    await deletePanel(String(panelId));
    router.refresh();
  };

  const handleImageUpload = async (panelId: number, file: File) => {
    const fd = new FormData();
    fd.set('file', file);
    await uploadPanelImage(String(panelId), fd);
    router.refresh();
  };

  const handleAttachAudio = async (panelId: number, line: VoiceLine) => {
    setPickerForPanel(null);
    await fetch('/api/panels/attach-audio', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ panelId, audioPath: line.audio_path }),
    });
    setPanelAudio(prev => ({ ...prev, [panelId]: line.audio_path }));
  };

  const handleDetachAudio = async (panelId: number) => {
    await fetch('/api/panels/attach-audio', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ panelId, audioPath: null }),
    });
    setPanelAudio(prev => ({ ...prev, [panelId]: null }));
  };

  return (
    <>
      {pickerForPanel !== null && (
        <VoicePicker
          onPick={line => handleAttachAudio(pickerForPanel, line)}
          onClose={() => setPickerForPanel(null)}
        />
      )}

      <div className="space-y-4">
        {initialPanels.map((panel, idx) => (
          <div key={panel.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">Panel {idx + 1}</span>
              <button
                onClick={() => handleDelete(panel.id)}
                className="p-1 text-slate-600 hover:text-red-400 transition-colors"
                title="Delete panel"
              >
                <Trash2 size={14} />
              </button>
            </div>

            <input
              value={localValues[panel.id]?.title ?? ''}
              onChange={e => handleFieldChange(panel.id, 'title', e.target.value)}
              placeholder="Panel title (optional)"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-purple-500/50"
            />

            <textarea
              value={localValues[panel.id]?.content ?? ''}
              onChange={e => handleFieldChange(panel.id, 'content', e.target.value)}
              placeholder="Write the scene here..."
              rows={4}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-purple-500/50 resize-y"
            />

            <div className="flex items-center gap-3 flex-wrap">
              {/* Image */}
              {panel.image_path && (
                <img src={`/${panel.image_path}`} alt="Panel image" className="w-20 h-20 object-cover rounded-lg border border-slate-700" />
              )}
              <button
                onClick={() => fileInputRefs.current[panel.id]?.click()}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-400 transition-colors"
              >
                <ImagePlus size={14} />
                {panel.image_path ? 'Change Image' : 'Add Image'}
              </button>
              <input
                type="file" accept="image/*" className="hidden"
                ref={el => { fileInputRefs.current[panel.id] = el; }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(panel.id, f); }}
              />

              {/* Voice line */}
              {!panelAudio[panel.id] ? (
                <button
                  onClick={() => setPickerForPanel(panel.id)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-400 transition-colors"
                >
                  <AudioLines size={14} /> Add Voice
                </button>
              ) : (
                <button
                  onClick={() => handleDetachAudio(panel.id)}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-400 transition-colors"
                  title="Remove voice line"
                >
                  <X size={12} /> Remove Voice
                </button>
              )}
            </div>

            {/* Attached voice player */}
            {panelAudio[panel.id] && (
              <div className="flex items-center gap-2 bg-purple-900/20 border border-purple-500/20 rounded-lg px-3 py-2">
                <AudioLines size={13} className="text-purple-400 shrink-0" />
                <audio controls src={`/${panelAudio[panel.id]}`} className="flex-1 h-7" />
              </div>
            )}
          </div>
        ))}

        <button
          onClick={handleAdd}
          className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-700 hover:border-slate-600 rounded-xl text-slate-500 hover:text-slate-400 transition-colors"
        >
          <Plus size={16} /> Add Panel
        </button>
      </div>
    </>
  );
}
